import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createTreeReplacement,
  fetchTreeReplacement,
  fetchTreeSpeciesOptions,
  updateTreeReplacement,
} from '../../../../api/tree-replacements/client';
import type {
  CreateTreeReplacementInput,
  UpdateTreeReplacementInput,
} from '../../../../api/tree-replacements/types';
import type { TreeReplacementDetail } from '../../../../api/workshops';
import { workshopKey } from '../useWorkshop';

const SPECIES_STALE_MS = 5 * 60_000;

export function useTreeSpeciesOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['tree-species', 'active'],
    queryFn: fetchTreeSpeciesOptions,
    enabled,
    staleTime: SPECIES_STALE_MS,
  });
}

export const treeReplacementKey = (workshopId: string, id: string) =>
  ['workshop', workshopId, 'tree-replacement', id] as const;

export function useTreeReplacementDetail(workshopId: string, id: string) {
  return useQuery({
    queryKey: treeReplacementKey(workshopId, id),
    queryFn: () => fetchTreeReplacement(workshopId, id),
    refetchOnMount: 'always',
  });
}

export function useCreateTreeReplacement(workshopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTreeReplacementInput) => createTreeReplacement(workshopId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: workshopKey(workshopId) }),
  });
}

export function useUpdateTreeReplacement(workshopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateTreeReplacementInput }) =>
      updateTreeReplacement(workshopId, id, patch),
    onSuccess: (saved, { id }) => {
      const key = treeReplacementKey(workshopId, id);
      qc.setQueryData<TreeReplacementDetail>(key, (old) => (old ? { ...old, ...saved } : old));
      void qc.invalidateQueries({ queryKey: key });
      return qc.invalidateQueries({ queryKey: workshopKey(workshopId) });
    },
  });
}
